import { api } from "../api";
import { formatearPesos } from "../../lib/money";
import { iconoImpresora } from "../iconos";
import { imprimirElemento, abrirRemito } from "../remito";
import { TURNOS } from "../../lib/types";
import type {
  ProduccionRespuesta,
  ProduccionItem,
  PedidoResumen,
  Turno,
} from "../../lib/types";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

function ddmmaaaa(iso: string): string {
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

export async function renderProduccion(contenedor: HTMLElement): Promise<void> {
  contenedor.innerHTML = `
    <div class="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 id="titulo" class="titulo-pagina">Producción</h1>
        <p class="subtitulo">Los pedidos del día y lo que hay que producir en cada turno.</p>
      </div>
      <div class="no-print flex items-end gap-2">
        <label><span class="etiqueta">Fecha</span><input id="fecha" type="date" class="campo" /></label>
        <button id="ver" class="btn-primario">Ver</button>
        <button id="imprimir" class="btn-sec">${iconoImpresora} Imprimir</button>
      </div>
    </div>
    <div id="salida" class="space-y-4"></div>
    <dialog id="dlgPedido"></dialog>`;

  const inpFecha = contenedor.querySelector("#fecha") as HTMLInputElement;
  const salida = contenedor.querySelector("#salida") as HTMLElement;
  const titulo = contenedor.querySelector("#titulo") as HTMLElement;
  const dlgPedido = contenedor.querySelector("#dlgPedido") as HTMLDialogElement;
  let ultima: ProduccionRespuesta | null = null;

  async function cargar(): Promise<void> {
    const q = inpFecha.value ? `?fecha=${inpFecha.value}` : "";
    salida.innerHTML = `<div class="tarjeta text-masa-400">Cargando...</div>`;
    try {
      const r = await api.get<ProduccionRespuesta>(`/api/produccion${q}`);
      inpFecha.value = r.fecha;
      titulo.textContent = `Producción del ${ddmmaaaa(r.fecha)}`;
      pintar(r);
    } catch (e) {
      salida.innerHTML = `<div class="tarjeta text-red-700">${esc(e instanceof Error ? e.message : "Error")}</div>`;
    }
  }

  /**
   * La pantalla son tres bloques, en este orden:
   *
   *   1. UN solo resumen de pedidos del dia, separado adentro en manana y
   *      tarde, con el total del dia al pie;
   *   2. la hoja de PRODUCCION MANANA, con su boton de imprimir;
   *   3. la de PRODUCCION TARDE, con el suyo.
   *
   * Antes el resumen se repetia dentro de cada turno, y como cada copia decia
   * "TOTAL DEL DIA" con el total de ESE turno, en el celular parecian dos dias
   * distintos. El total del dia es uno solo y va una sola vez.
   *
   * Habia ademas una matriz por area con una columna por cliente. Se saco: con
   * un solo local repetia la misma cifra en dos columnas, y en el celular
   * obligaba a scrollear de costado. Lo que se pide para cada local se ve en su
   * remito, a un toque desde el resumen.
   */
  function pintar(r: ProduccionRespuesta): void {
    if (r.items.length === 0) {
      salida.innerHTML = `<div class="tarjeta text-center text-masa-400">Sin producción para ${esc(ddmmaaaa(r.fecha))}.</div>`;
      return;
    }
    ultima = r;

    const hojas = TURNOS.map(({ valor, nombre }) => {
      const items = r.items.filter((i) => i.turno === valor);
      return items.length === 0 ? "" : cardProduccion(items, valor, nombre);
    }).join("");

    salida.innerHTML = resumenDelDia(r.pedidos) + hojas;
  }

  // Tablas limpias por area: solo Articulo | Bultos | Unidades (lo que hornea
  // la cuadra, sin el desglose por cliente). Compartido por pantalla e impresion.
  function tablasProduccion(items: ProduccionItem[]): string {
    const porArea = new Map<string, ProduccionItem[]>();
    for (const it of items) {
      const arr = porArea.get(it.area) ?? [];
      arr.push(it);
      porArea.set(it.area, arr);
    }
    const bloques: string[] = [];
    for (const [area, its] of porArea) {
      const filas = its
        .map(
          (it) => `<tr class="border-t border-black/10">
            <td class="px-2 py-1.5">${esc(it.descripcion)}</td>
            <td class="px-2 py-1.5 text-right font-bold tabular-nums">${it.unidades}</td>
          </tr>`,
        )
        .join("");
      bloques.push(`
        <div class="mb-3 break-inside-avoid">
          <h3 class="mb-1 border-b-2 border-masa-600 text-sm font-bold uppercase tracking-wide text-masa-700">${esc(area)}</h3>
          <table class="w-full text-sm">
            <thead class="text-[11px] uppercase tracking-wider text-masa-400"><tr>
              <th class="px-2 text-left font-normal">Artículo</th>
              <th class="px-2 text-right font-normal">Unidades</th>
            </tr></thead>
            <tbody>${filas}</tbody>
          </table>
        </div>`);
    }
    return bloques.join("");
  }

  // Tarjeta grande "PRODUCCION" con su propio boton de imprimir (solo lo que hay
  // que producir, de ESTE turno). El boton de arriba imprime TODA la pagina.
  function cardProduccion(items: ProduccionItem[], turno: Turno, nombre: string): string {
    return `
      <div class="tarjeta">
        <div class="mb-3 flex items-center justify-between gap-2">
          <h3 class="text-3xl font-extrabold uppercase tracking-tight text-masa-800">Producción ${esc(nombre)}</h3>
          <button data-imprimir-prod="${turno}" class="btn-primario">${iconoImpresora} Imprimir</button>
        </div>
        <div class="columns-1 sm:columns-2" style="column-gap:1.5rem">${tablasProduccion(items)}</div>
      </div>`;
  }

  /**
   * El unico resumen de la pantalla: todos los pedidos del dia, agrupados por
   * turno, con el subtotal de cada uno y el total del dia al pie.
   *
   * Va arriba de todo y una sola vez. Cada fila se puede tocar para abrir el
   * remito de ese local.
   */
  function resumenDelDia(pedidos: PedidoResumen[]): string {
    const totalDia = pedidos.reduce((a, p) => a + p.total, 0);

    const grupos = TURNOS.map(({ valor, nombre }) => {
      const delTurno = pedidos.filter((p) => p.turno === valor);
      if (delTurno.length === 0) return "";
      const subtotal = delTurno.reduce((a, p) => a + p.total, 0);
      const filas = delTurno
        .map(
          (p) => `<tr class="cursor-pointer border-t border-crema-100 hover:bg-crema-50" data-ver-pedido="${p.id}" title="Ver detalle">
            <td class="py-1 pr-2 font-medium text-masa-700">${esc(p.cliente)}</td>
            <td class="py-1 pr-2 text-masa-400">#${p.id}</td>
            <td class="py-1 text-right tabular-nums">${formatearPesos(p.total)}</td>
          </tr>`,
        )
        .join("");
      return `
        <tr><td colspan="3" class="pb-1 pt-4 text-sm font-extrabold uppercase tracking-wide text-masa-800">
          ${esc(nombre)}
          <span class="font-normal normal-case tracking-normal text-masa-400">· ${delTurno.length} pedido${delTurno.length === 1 ? "" : "s"}</span>
        </td></tr>
        ${filas}
        <tr class="border-t border-crema-200">
          <td class="py-1 pr-2 text-xs text-masa-500" colspan="2">Subtotal ${esc(nombre.toLowerCase())}</td>
          <td class="py-1 text-right text-sm font-semibold tabular-nums text-masa-700">${formatearPesos(subtotal)}</td>
        </tr>`;
    }).join("");

    return `
      <div class="tarjeta">
        <h2 class="text-sm font-bold uppercase tracking-wide text-masa-600">Resumen de pedidos del día <span class="font-normal normal-case text-masa-400">(tocá un cliente para ver e imprimir su remito)</span></h2>
        <table class="w-full text-sm">
          <tbody>${
            grupos ||
            `<tr><td class="py-4 text-center text-masa-400">Sin pedidos confirmados para esta fecha.</td></tr>`
          }</tbody>
          <tfoot><tr class="border-t-2 border-masa-300 font-bold">
            <td class="py-2" colspan="2">TOTAL DEL DÍA</td>
            <td class="py-2 text-right tabular-nums">${formatearPesos(totalDia)}</td>
          </tr></tfoot>
        </table>
      </div>`;
  }

  // El remito (2 vias) vive en ../remito.ts: lo comparten esta pantalla y la
  // bandeja de Pedidos web, y tiene que salir identico en las dos.
  const verPedido = (id: number): Promise<void> => abrirRemito(dlgPedido, id);

  salida.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement;
    const btnProd = target.closest<HTMLElement>("[data-imprimir-prod]");
    if (btnProd && ultima) {
      const turno = btnProd.dataset.imprimirProd as Turno;
      const nombre = TURNOS.find((t) => t.valor === turno)?.nombre ?? "";
      const items = ultima.items.filter((i) => i.turno === turno);
      const html = `
        <div class="mx-auto max-w-2xl p-4 text-black">
          <h1 class="mb-3 text-2xl font-extrabold uppercase">Producción del ${ddmmaaaa(ultima.fecha)} — ${esc(nombre)}</h1>
          <div class="columns-1 sm:columns-2" style="column-gap:1.5rem">${tablasProduccion(items)}</div>
        </div>`;
      imprimirElemento(html);
      return;
    }
    const row = target.closest("[data-ver-pedido]");
    if (row) void verPedido(Number(row.getAttribute("data-ver-pedido")));
  });

  contenedor.querySelector("#ver")?.addEventListener("click", () => void cargar());
  contenedor.querySelector("#imprimir")?.addEventListener("click", () => window.print());
  await cargar();
}
